import React from "react";
import { ErrorList } from "components/Posts/PostForm/PostFieldError/PostFieldError";
import { Notification } from "@/components/ui/notification";
import { fbMinimum } from "components/Posts/PostForm/postFormValidator";
import { getMediaFileDuration } from "@/entities/media-files";
import { PostAssetProps } from "./post-selected-assets";
import { MediaFilesErrors, MediaFilesWarning } from "../../validation";

interface Props {
  warnings: MediaFilesWarning;
  errors: MediaFilesErrors;
  files: PostAssetProps[];
  className?: string;
}

function WarningsErrorsComponent(props: Props) {
  const piVideosInCarouselIndexes =
    props.errors.PIVideosInCarouselIndexes ?? [];

  return (
    <div className={props.className}>
      <ErrorList>
        {props.errors.tooManyFiles && (
          <Notification
            variant="error"
            title={`Too many files - we support up to ${props.errors.tooManyFiles || 10} files in this post.`}
          />
        )}
        {props.errors.IGVideoTooShortVideoIndexes && (
          <Notification
            variant="error"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              props.errors.IGVideoTooShortVideoIndexes
            )}) are less than the Instagram minimum of 3 seconds long.`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>
                  Instagram requires videos to be at least 3 seconds long.
                </span>
                <VideoFilesDurations
                  files={props.files}
                  indexes={props.errors.IGVideoTooShortVideoIndexes}
                />
              </div>
            }
          />
        )}

        {props.errors.IGStoryVideoTooShortVideoIndexes && (
          <Notification
            variant="error"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              props.errors.IGStoryVideoTooShortVideoIndexes
            )}) are less than the Instagram Story minimum of 3 seconds long.`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>
                  Instagram story posts must be at least 3 seconds long.
                </span>
                <VideoFilesDurations
                  files={props.files}
                  indexes={props.errors.IGStoryVideoTooShortVideoIndexes}
                />
              </div>
            }
          />
        )}

        {props.errors.IGStoryVideoTooLongVideoIndexes && (
          <Notification
            variant="error"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              props.errors.IGStoryVideoTooLongVideoIndexes
            )}) are more than the Instagram Story maximum of 60 seconds long.`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>
                  Instagram stories must be no more than 60 seconds long.
                  Alternatively, set the post to manual posting and you can post
                  it manually, cropping it at upload in the Instagram app.
                </span>
                <VideoFilesDurations
                  files={props.files}
                  indexes={props.errors.IGStoryVideoTooLongVideoIndexes}
                />
              </div>
            }
          />
        )}

        {props.errors.FBVideoTooLongVideoIndexes && (
          <Notification
            variant="error"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              props.errors.FBVideoTooLongVideoIndexes
            )}) are more than the Facebook maximum of 240 minutes.`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>
                  Facebook requires videos to be no more than 240 minutes.
                </span>
                <VideoFilesDurations
                  files={props.files}
                  indexes={props.errors.FBVideoTooLongVideoIndexes}
                />
              </div>
            }
          />
        )}

        {props.errors.LIVideosInCarouselIndexes && (
          <Notification
            variant="error"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              props.errors.LIVideosInCarouselIndexes
            )}) are videos, and can not be posted to LinkedIn as a carousel.`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>
                  The LinkedIn API supports only images in carousels, so either
                  add images to post an image carousel or remove video(s) to
                  only have a single video.
                </span>
                <VideoFilesDurations
                  files={props.files}
                  indexes={props.errors.LIVideosInCarouselIndexes}
                />
              </div>
            }
          />
        )}

        {props.errors.LIImagesLimit && (
          <Notification
            variant="error"
            title="LinkedIn only supports up to 9 images in a post. Please reduce the number of images in this post for the LinkedIn post."
          />
        )}

        {props.errors.LIDocumentsLimit && (
          <Notification
            variant="error"
            title="Document posts for LinkedIn can only have one attached document, and cannot have more than one type of document."
          />
        )}

        {props.errors.TTVideoTooMany && (
          <Notification
            variant="error"
            title="TikTok only supports a single video post. Posts with multiple videos cannot be saved for TikTok."
          />
        )}

        {props.errors.TTMixedPhotoVideo && (
          <Notification
            variant="error"
            title="TikTok posts must contain either a single video or only images. Mixed photo and video posts cannot be saved for TikTok."
          />
        )}

        {piVideosInCarouselIndexes.length > 0 && (
          <Notification
            variant="error"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              piVideosInCarouselIndexes
            )}) are videos, and cannot be posted to Pinterest as a carousel.`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>
                  Pinterest carousel posts support 2-5 images only. Remove
                  videos or use a single video Pin.
                </span>
                <VideoFilesDurations
                  files={props.files}
                  indexes={piVideosInCarouselIndexes}
                />
              </div>
            }
          />
        )}

        {props.errors.PIImagesOverLimit && (
          <Notification
            variant="error"
            title="Pinterest carousel posts support 2-5 images only. Remove extra images before saving this post."
          />
        )}

        {props.errors.TWImagesLimit && (
          <Notification
            variant="error"
            title="Twitter only supports up to 4 images in a carousel. As there are no images in this carousel, the post will not post to Twitter."
          />
        )}

        {props.errors.TWImagesOverLimit && (
          <Notification
            variant="error"
            title="Twitter only supports up to 4 images in a carousel. As there are more than 4 images in this carousel, the post will not post to Twitter."
          />
        )}

        {props.errors.TWVideoTooLongIndexes && (
          <Notification
            variant="error"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              props.errors.TWVideoTooLongIndexes
            )}) are more than the Twitter maximum of 2 minutes and 20 seconds (140 seconds total).`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>
                  Twitter videos can be at most 2 minutes and 20 seconds (140
                  seconds total).
                </span>
                <VideoFilesDurations
                  files={props.files}
                  indexes={props.errors.TWVideoTooLongIndexes}
                />
              </div>
            }
          />
        )}

        {props.errors.THVideoTooWideIndexes && (
          <Notification
            variant="error"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              props.errors.THVideoTooWideIndexes
            )}) are more than the Threads maximum width of 1920px.`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>Threads videos can be at most 1920px wide.</span>
                <FilesDimensions
                  files={props.files}
                  indexes={props.errors.THVideoTooWideIndexes}
                />
              </div>
            }
          />
        )}
        {props.errors.THVideoTooShortIndexes && (
          <Notification
            variant="error"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              props.errors.THVideoTooShortIndexes
            )}) are less than the Threads minimum length of 1 second.`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>Threads videos should be at least 1 second long.</span>
                <FilesDimensions
                  files={props.files}
                  indexes={props.errors.THVideoTooShortIndexes}
                />
              </div>
            }
          />
        )}
        {props.errors.THVideoTooLongIndexes && (
          <Notification
            variant="error"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              props.errors.THVideoTooLongIndexes
            )}) are greater than the Threads maximum length of 300 seconds.`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>Threads videos should be at most 5 minutes long.</span>
                <FilesDimensions
                  files={props.files}
                  indexes={props.errors.THVideoTooLongIndexes}
                />
              </div>
            }
          />
        )}
        {props.errors.THVideoTooBigIndexes && (
          <Notification
            variant="error"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              props.errors.THVideoTooBigIndexes
            )}) are greater than the Threads maximum size of 1GB.`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>Threads videos should be at most 1GB in size.</span>
                <FilesDimensions
                  files={props.files}
                  indexes={props.errors.THVideoTooBigIndexes}
                />
              </div>
            }
          />
        )}
        {props.errors.THImageTooBigIndexes && (
          <Notification
            variant="error"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              props.errors.THImageTooBigIndexes
            )}) are greater than the Threads maximum size of 8MB.`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>Threads images should be at most 8MB in size.</span>
                <FilesDimensions
                  files={props.files}
                  indexes={props.errors.THImageTooBigIndexes}
                />
              </div>
            }
          />
        )}

        {props.errors.GMBHasVideo && (
          <Notification
            variant="error"
            title="Google only supports images (not videos) uploaded via their API, and so we cannot upload this post to your Google Business Profile."
          />
        )}
        {props.errors.GMBResolutionIndexes?.length && (
          <Notification
            variant="error"
            expandable
            defaultExpanded={false}
            title={`File ${renderFilesIndexes(
              props.errors.GMBResolutionIndexes
            )} is less than GMB's minimum size`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>
                  Google Business Profile will use the first supported image
                  only, and it must be at least 250x250 pixels before you can
                  save this post.
                </span>
                <FilesDimensions
                  files={props.files}
                  indexes={props.errors.GMBResolutionIndexes}
                />
              </div>
            }
          />
        )}
        {props.errors.GMBFileSizeIndexes?.length && (
          <Notification
            variant="error"
            expandable
            defaultExpanded={false}
            title={`File ${renderFilesIndexes(
              props.errors.GMBFileSizeIndexes
            )} is below GMB's minimum file size`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>
                  Google Business Profile will use the first supported image
                  only, and it must be at least 10KB before you can save this
                  post.
                </span>
                <FilesByteSizes
                  files={props.files}
                  indexes={props.errors.GMBFileSizeIndexes}
                />
              </div>
            }
          />
        )}

        {props.errors.IGStoryItemsCount && (
          <Notification
            variant="error"
            title="You can only automatically post story chains up to 3 items long. This ensures that your stories are successfully published. You can split longer stories 15 minutes apart, or publish manually for more items."
          />
        )}
        {props.warnings.notSameRatioIndexes?.length && (
          <Notification
            variant="warning"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              props.warnings.notSameRatioIndexes
            )}) are not the same aspect ratio as the first image, they may be cropped by Instagram.`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>
                  Instagram crops all posts to be the same aspect ratio as the
                  first file we select, so they may appear cropped/distorted.
                </span>
                <p className="font-bold text-sm">
                  Tip: You may want to crop files using our photo editor before
                  publishing.
                </p>
              </div>
            }
          />
        )}
        {props.warnings.IGRatioRecommendationIndexes?.length && (
          <Notification
            variant="warning"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              props.warnings.IGRatioRecommendationIndexes
            )}) are outside of recommended aspect ratio, they may be cropped by Instagram.`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>
                  The recommended aspect ratio is from 4:5 (eg 1080x1350) to
                  1.91:1 (eg 1080x566). Files outside of these ratios may be
                  cropped when published.
                </span>
                <FilesDimensions
                  files={props.files}
                  indexes={props.warnings.IGRatioRecommendationIndexes}
                />
                <p className="font-bold text-sm">
                  Tip: You may want to crop files using our photo editor before
                  publishing.
                </p>
              </div>
            }
          />
        )}
        {props.warnings.IGStoryRatioIndexes?.length && (
          <Notification
            variant="warning"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              props.warnings.IGStoryRatioIndexes
            )}) is a different aspect ratio to our story ratios`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>
                  Aspect ratio to our story ratios for auto-publishing should be
                  1836x3019, 1080x1776 or alternatively 1080x1920. You may see
                  some cropping when this is auto-published. Consider cropping
                  the story, or using the manual upload feature.
                </span>
                <FilesDimensions
                  files={props.files}
                  indexes={props.warnings.IGStoryRatioIndexes}
                />
              </div>
            }
          />
        )}
        {props.warnings.IGResolutionIndexes?.length && (
          <Notification
            variant="warning"
            expandable
            defaultExpanded={false}
            title={`Assets (${renderFilesIndexes(
              props.warnings.IGResolutionIndexes
            )}) have width less than Instagram's recommended size, they may be pixelated.`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>
                  The minimum recommended Instagram's resolution is 1080 pixels.
                  Using lower resolution files may result in a pixelated,
                  low-resolution post. We suggest following Instagram's
                  recommendations for best quality.
                </span>
                <FilesDimensions
                  files={props.files}
                  indexes={props.warnings.IGResolutionIndexes}
                />
              </div>
            }
          />
        )}
        {props.warnings.THResolutionImageIndexes?.length && (
          <Notification
            variant="warning"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              props.warnings.THResolutionImageIndexes
            )}) are outside the Threads image resolution requirements`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>
                  Threads images should be between 360 and 1440 pixels wide.
                  Images will be resized by Threads Platform to fit within these
                  dimensions.
                </span>
                <FilesDimensions
                  files={props.files}
                  indexes={props.warnings.THResolutionImageIndexes}
                />
              </div>
            }
          />
        )}
        {props.warnings.FBResolutionIndexes?.length && (
          <Notification
            variant="warning"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              props.warnings.FBResolutionIndexes
            )}) are less than Facebook's minimum ${fbMinimum} pixel width`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>
                  Facebook's minimum is {fbMinimum} pixel width. You must upload
                  higher resolution file(s) for this to post.
                </span>
                <FilesDimensions
                  files={props.files}
                  indexes={props.warnings.FBResolutionIndexes}
                />
              </div>
            }
          />
        )}
        {props.warnings.FBVideoIndexes?.length && (
          <Notification
            variant="warning"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              props.warnings.FBVideoIndexes
            )}) are videos, and may not be posted to Facebook`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>
                  The Facebook API supports only images in carousels/albums. If
                  you have only videos in this carousel, we will post the first
                  video only.
                </span>
              </div>
            }
          />
        )}
        {props.warnings.LIVideosInCarouselIndexes?.length && (
          <Notification
            variant="warning"
            expandable
            defaultExpanded={false}
            title={`Files (${renderFilesIndexes(
              props.warnings.LIVideosInCarouselIndexes
            )}) are videos, and will not be posted to LinkedIn.`}
            description={
              <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                <span>
                  The LinkedIn API supports only images in carousels (and so we
                  will only post your images) - you can post a single video post
                  though.
                </span>
              </div>
            }
          />
        )}
        {props.warnings.TWHasVideoInCarousel && (
          <Notification
            variant="warning"
            title="Twitter only supports up to 4 images in a carousel. We will only post the image(s) in the carousel to your Twitter account(s)."
          />
        )}
        {props.warnings.TWFilesLimit && (
          <Notification
            variant="warning"
            title={`You have ${props.files.length} files selected. Twitter only supports the first 4 images in a carousel, so we will only post the first 4 images.`}
          />
        )}
        {props.warnings.GMBHasVideo && (
          <Notification
            variant="warning"
            title="Google only supports images (not videos) uploaded via their API, and so your video(s) will not be included in the Google Business Profile post."
          />
        )}
        {props.warnings.GMBHasMoreOneImages && (
          <Notification
            variant="warning"
            title="Google only supports (up to) one image (and no videos) in posts made via their API. We will publish this post to Google Business Profile with the first image only."
          />
        )}
        {props.warnings.THDocumentNotSupported && (
          <Notification
            variant="warning"
            title="Threads does not support documents and it will be ignored."
          />
        )}
        {[
          props.errors.FBVideoTooShortVideoIndexes,
          props.errors.TWVideoTooShortIndexes,
        ].some(Boolean) && (
          <PlatformsCommonMessage
            facebook={props.errors.FBVideoTooShortVideoIndexes}
            twitter={props.errors.TWVideoTooShortIndexes}
          >
            {(platformsString, indexes) => (
              <Notification
                variant="error"
                expandable
                defaultExpanded={false}
                title={`Files (${renderFilesIndexes(
                  indexes
                )}) are less than ${platformsString}'s minimum of 1 second.`}
                description={
                  <div className="grid grid-cols-1 gap-3.5 text-neutral-800">
                    <span>
                      {platformsString} requires videos to be at least 1 seconds
                      long.
                    </span>
                    <VideoFilesDurations
                      indexes={indexes}
                      files={props.files}
                    />
                  </div>
                }
              />
            )}
          </PlatformsCommonMessage>
        )}
        <TTWarningsErrors
          warnings={props.warnings}
          errors={props.errors}
          files={props.files}
        />
      </ErrorList>
    </div>
  );
}

const TTWarningsErrors = ({
  warnings,
  errors: _errors,
  files: _files,
}: {
  warnings: MediaFilesWarning;
  errors: MediaFilesErrors;
  files: PostAssetProps[];
}) => {
  return (
    <>
      {warnings.TTUnsupportedMediaFile && (
        <Notification
          variant="warning"
          title="TikTok does not support document files. Documents will not be included in TikTok posts."
        />
      )}
    </>
  );
};

export const WarningsErrors = WarningsErrorsComponent;

function FilesDimensions(props: {
  files: PostAssetProps[];
  indexes: number[];
}) {
  return (
    <ul className="list-disc pl-4">
      {props.indexes.map((index) => {
        const file = props.files[index];
        if (!file?.meta) return null;
        return (
          <li key={file.thumbnailUrl} className="text-sm leading-[17px]">
            File {index + 1} has dimensions {file.meta.width} x{" "}
            {file.meta.height}
          </li>
        );
      })}
    </ul>
  );
}

function FilesByteSizes(props: { files: PostAssetProps[]; indexes: number[] }) {
  return (
    <ul className="list-disc pl-4">
      {props.indexes.map((index) => {
        const file = props.files[index];
        const byteSize = file?.meta?.byteSize;
        if (typeof byteSize !== "number") return null;
        return (
          <li
            key={file.thumbnailUrl || index}
            className="text-sm leading-[17px]"
          >
            File {index + 1} is {(byteSize / 1024).toFixed(1)} KB ({byteSize}{" "}
            bytes)
          </li>
        );
      })}
    </ul>
  );
}

function VideoFilesDurations(props: {
  files: PostAssetProps[];
  indexes: number[];
}) {
  return (
    <ul className="list-disc pl-4">
      {props.indexes.map((index) => {
        const file = props.files[index];
        if (!file) return null;
        const duration = getMediaFileDuration(file as never);
        if (!duration) return null;
        return (
          <li key={index} className="text-sm leading-[17px]">
            File {index + 1} is {duration} seconds in length
          </li>
        );
      })}
    </ul>
  );
}

function renderFilesIndexes(indexes: number[]) {
  return indexes.map((index) => index + 1).join(", ");
}

function PlatformsCommonMessage(props: {
  facebook?: number[];
  twitter?: number[];
  children(
    platformsString: React.ReactNode,
    indexes: number[]
  ): React.ReactNode;
}) {
  const { children, ...platformsFilesIndexes } = props;

  const platforms = Object.keys(platformsFilesIndexes).filter((platform) =>
    Boolean(
      platformsFilesIndexes[platform as keyof typeof platformsFilesIndexes]
    )
  );

  if (platforms.length === 0) {
    return null;
  }
  const platformsNameMap: Record<string, string> = {
    facebook: "Facebook",
    twitter: "Twitter",
  };

  const platformsString = platforms
    .map((platform) => platformsNameMap[platform])
    .join(" & ");

  const indexes =
    platformsFilesIndexes[platforms[0] as keyof typeof platformsFilesIndexes];

  return <>{children(platformsString, indexes!)}</>;
}
